import { useState, useRef } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  CircularProgress,
} from '@mui/material';
import type { CreateProductRequest } from '../../../types/product';
import { MIN_SEARCH_LENGTH } from '../../../utils/searchConstants';
import ProductCodeField from './ProductCodeField';

interface CreateProductProps {
  open: boolean;
  companyId: number;
  onClose: () => void;
  onSubmit: (productData: CreateProductRequest) => Promise<void>;
}

export default function CreateProduct({
  open,
  companyId,
  onClose,
  onSubmit,
}: CreateProductProps) {
  const [name, setName] = useState('');
  const [code, setCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const handleTransitionEnd = () => {
    if (open && inputRef.current) {
      inputRef.current.focus();
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!name.trim()) {
      setError('Назва продукту обов\'язкова');
      return;
    }

    if (name.trim().length < MIN_SEARCH_LENGTH) {
      setError(`Назва продукту має містити щонайменше ${MIN_SEARCH_LENGTH} символів`);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      await onSubmit({
        companyId,
        name: name.trim(),
        code: code.trim() || undefined,
      });
      
      // Reset form and close dialog on success
      setName('');
      setCode('');
      onClose();
    } catch (err) {
      console.error('Error creating product:', err);
      setError('Не вдалося створити продукт. Спробуйте ще раз.');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      setName('');
      setCode('');
      setError(null);
      onClose();
    }
  };

  return (
    <Dialog 
      open={open} 
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      slotProps={{
        transition: {
          onEntered: handleTransitionEnd,
        }
      }}
    >
      <form onSubmit={handleSubmit}>
        <DialogTitle>Створити новий продукт</DialogTitle>
        <DialogContent>
          <TextField
            inputRef={inputRef}
            fullWidth
            label="Назва продукту"
            value={name}
            onChange={(e) => setName(e.target.value)}
            error={!!error}
            helperText={error}
            disabled={loading}
            required
            margin="normal"
          />
          <ProductCodeField
            label="Код продукту (SKU)"
            value={code}
            onChange={setCode}
            disabled={loading}
            helperText="Необов'язковий унікальний ідентифікатор"
          />
        </DialogContent>

        <DialogActions>
          <Button onClick={handleClose} disabled={loading}>
            Скасувати
          </Button>
          <Button
            type="submit"
            variant="contained"
            disabled={loading || !name.trim()}
          >
            {loading ? <CircularProgress size={24} /> : 'Створити'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
}
